import React, { useEffect, useRef } from 'react';
import { layer, data, source } from 'azure-maps-control';
import { drawing, control } from 'azure-maps-drawing-tools';
import 'azure-maps-drawing-tools/dist/atlas-drawing.min.css';
import WasteSelectorTypes from 'components/waste-icons/WasteSelectorTypes';

const AzureMapZones = ({
    map,
    assets = [],
    zones = [],
    editZone = null,
    onPolygonCreated,
    onPolygonEdited,
    onPolygonDeleted
}) => {
    const zonesDataSourceRef = useRef(null);
    const assetsDataSourceRef = useRef(null);
    const editDataSourceRef = useRef(null);
    const drawingManagerRef = useRef(null);

    useEffect(() => {
        if (!map) return;

        // Create data sources
        const zonesDataSource = new source.DataSource();
        const assetsDataSource = new source.DataSource();
        const editDataSource = new source.DataSource();

        map.sources.add([zonesDataSource, assetsDataSource, editDataSource]);

        zonesDataSourceRef.current = zonesDataSource;
        assetsDataSourceRef.current = assetsDataSource;
        editDataSourceRef.current = editDataSource;

        // Add layers for static zones
        map.layers.add([
            new layer.PolygonLayer(zonesDataSource, null, {
                fillColor: '#a3a3a3',
                fillOpacity: 0.5
            }),
            new layer.LineLayer(zonesDataSource, null, {
                color: '#8d9091',
                strokeWidth: 4
            })
        ]);

        // Add layer for assets (circles)
        map.layers.add(new layer.BubbleLayer(assetsDataSource, null, {
            color: ['get', 'borderColor'],
            fillColor: ['get', 'fillColor'],
            fillOpacity: 0.8,
            strokeWidth: 1,
        }));

        // Initialize drawing manager
        const drawingManager = new drawing.DrawingManager(map, {
            source: editDataSource,
            interactionType: 'click',
            dragHandleStyle: {
                htmlContent: '<div style="width: 24px; height: 24px; border-radius: 50%; background-color: #fff; border: 2px solid #3388ff; opacity: 0.6; cursor: pointer;"></div>',
                anchor: 'center'
            },
            secondaryDragHandleStyle: {
                htmlContent: '<div style="width: 16px; height: 16px; border-radius: 50%; background-color: #fff; border: 2px solid #a3a3a3; opacity: 0.4; cursor: pointer;"></div>',
                anchor: 'center'
            },
            toolbar: new control.DrawingToolbar({
                buttons: ['draw-polygon', 'edit-geometry', 'erase-geometry'],
                position: 'top-right',
                style: 'light'
            })
        });
        drawingManagerRef.current = drawingManager;

        const extractAndCloseCoordinates = (shape) => {
            const feature = shape.toJson();
            if (feature.geometry.type === 'Polygon') {
                let coords = feature.geometry.coordinates[0].map(coord => ({
                    lat: coord[1],
                    lng: coord[0]
                }));

                if (coords.length > 0) {
                    const first = coords[0];
                    const last = coords[coords.length - 1];
                    if (first.lat !== last.lat || first.lng !== last.lng) {
                        coords.push({ ...first });
                    }
                }

                return coords.length >= 4 ? coords : null;
            }
            return null;
        };

        const onDrawingComplete = (shape) => {
            const coords = extractAndCloseCoordinates(shape);
            if (onPolygonCreated && coords) {
                onPolygonCreated(coords);
            }
        };

        const onDrawingChanged = (shape) => {
            const coords = extractAndCloseCoordinates(shape);
            if (onPolygonEdited && coords) {
                onPolygonEdited(coords);
            }
        };

        const onDrawingErased = (shape) => {
            if (onPolygonCreated) {
                onPolygonCreated([]);
            }
            if (onPolygonDeleted) {
                onPolygonDeleted();
            }
        };

        // Event: Polygon created
        map.events.add('drawingcomplete', drawingManager, onDrawingComplete);

        // Event: Polygon edited/moved
        map.events.add('drawingchanged', drawingManager, onDrawingChanged);

        // Event: Polygon deleted
        map.events.add('drawingerased', drawingManager, onDrawingErased);

        return () => {
            // Dispose drawing manager first to clean up its internal layers
            if (drawingManagerRef.current) {
                try {
                    drawingManagerRef.current.dispose();
                } catch (e) {
                    console.warn("DrawingManager dispose error:", e);
                }
                drawingManagerRef.current = null;
            }

            // Unregister events and remove sources
            if (map && !map.closed) {
                try {
                    map.events.remove('drawingcomplete', drawingManager, onDrawingComplete);
                    map.events.remove('drawingchanged', drawingManager, onDrawingChanged);
                    map.events.remove('drawingerased', drawingManager, onDrawingErased);

                    // Remove sources only if they exist on the map
                    const currentSources = map.sources.getSources();
                    const sourcesToRemove = [zonesDataSource, assetsDataSource, editDataSource].filter(s => currentSources.includes(s));
                    if (sourcesToRemove.length > 0) {
                        map.sources.remove(sourcesToRemove);
                    }
                } catch (e) {
                    console.warn("AzureMapZones cleanup warning:", e);
                }
            }
        };
    }, [map]);

    // Update assets and zones
    useEffect(() => {
        if (!zonesDataSourceRef.current || !assetsDataSourceRef.current || !editDataSourceRef.current) return;

        const zonesDataSource = zonesDataSourceRef.current;
        const assetsDataSource = assetsDataSourceRef.current;
        const editDataSource = editDataSourceRef.current;

        zonesDataSource.clear();
        assetsDataSource.clear();
        editDataSource.clear();

        // Add assets
        const assetFeatures = assets.map(asset => {
            const styles = WasteSelectorTypes.getStyle(asset.waste_type);
            return new data.Feature(new data.Point([asset.lng, asset.lat]), {
                ...asset,
                borderColor: styles.borderColor,
                fillColor: styles.fillColor
            });
        });
        assetsDataSource.add(assetFeatures);

        // Add existing zones
        const zoneFeatures = zones.map(zone => {
            const coordinates = [zone.polylines.map(p => [p.lng, p.lat])];
            // Ensure closed loop
            if (coordinates[0].length > 0 &&
                (coordinates[0][0][0] !== coordinates[0][coordinates[0].length - 1][0] ||
                    coordinates[0][0][1] !== coordinates[0][coordinates[0].length - 1][1])) {
                coordinates[0].push(coordinates[0][0]);
            }
            return new data.Feature(new data.Polygon(coordinates), {
                id: zone.id,
                name: zone.name,
                description: zone.description
            });
        });
        zonesDataSource.add(zoneFeatures);

        // Add edit zone
        if (editZone && editZone.polylines && editZone.polylines.length > 0) {
            const editCoords = [editZone.polylines.map(p => [p.lng, p.lat])];
            if (editCoords[0].length > 0 &&
                (editCoords[0][0][0] !== editCoords[0][editCoords[0].length - 1][0] ||
                    editCoords[0][0][1] !== editCoords[0][editCoords[0].length - 1][1])) {
                editCoords[0].push(editCoords[0][0]);
            }
            const editFeature = new data.Feature(new data.Polygon(editCoords), {
                id: 'edit-zone',
                name: editZone.name
            });
            editDataSource.add(editFeature);

            // If we have an edit zone, we might want to automatically enter edit mode
            // drawingManagerRef.current.setOptions({ mode: 'edit-geometry' });
            // But usually the user clicks the toolbar.
        }
    }, [assets, zones, editZone]);

    return null;
};

export default AzureMapZones;
